import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import AuthLayout from './components/AuthLayout';
import { resendVerificationEmail } from './api/auth';

type Status = 'success' | 'already' | 'invalid' | null;

const CONFIG: Record<NonNullable<Status>, {
  iconBg: string;
  icon: React.ReactNode;
  title: string;
  body: string;
  cta?: string;
}> = {
  success: {
    iconBg: 'bg-emerald-100',
    icon: (
      <svg className="w-9 h-9 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    title: 'Email verified!',
    body: "Your account is now active. You're all set to start finding decision makers.",
    cta: 'Get started →',
  },
  already: {
    iconBg: 'bg-brand-100',
    icon: (
      <svg className="w-9 h-9 text-brand-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    title: 'Already verified',
    body: 'Your email has already been verified. Log in to access your account.',
    cta: 'Go to login',
  },
  invalid: {
    iconBg: 'bg-amber-100',
    icon: (
      <svg className="w-9 h-9 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    title: 'Link expired',
    body: 'This verification link has expired or is no longer valid. Enter your email to request a new one.',
  },
};

export default function VerifyEmailCallback() {
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status') as Status;
  const config = status ? CONFIG[status] : null;

  const [email, setEmail] = useState('');
  const [isResending, setIsResending] = useState(false);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setIsResending(true);
    try {
      await resendVerificationEmail(email);
      toast.success('Verification email sent. Please check your inbox.');
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      if (axiosError.response?.status === 422) {
        toast.info(axiosError.response.data.message || 'Email is already verified.');
      } else {
        toast.error('Failed to send. Please try again.');
      }
    } finally {
      setIsResending(false);
    }
  };

  if (!config) {
    return (
      <AuthLayout title="Email Verification" subtitle="">
        <p className="text-center text-sm text-slate-500">Invalid verification status.</p>
        <p className="mt-4 text-center">
          <Link to="/login" className="text-sm font-bold text-brand-700 hover:text-brand-900">
            Back to login
          </Link>
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="" subtitle="">
      <div className="text-center">

        {/* Icon */}
        <div className={`w-20 h-20 ${config.iconBg} rounded-3xl flex items-center justify-center mx-auto mb-6`}>
          {config.icon}
        </div>

        <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight mb-3">{config.title}</h2>
        <p className="text-sm text-slate-500 leading-relaxed mb-8 max-w-xs mx-auto">{config.body}</p>

        {/* Invalid: resend form */}
        {status === 'invalid' && (
          <form onSubmit={handleResend} className="space-y-3 text-left mb-6">
            <input
              type="email"
              required
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3.5 border border-slate-200 hover:border-slate-300 rounded-xl text-sm text-slate-900 placeholder-slate-400 outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-all"
            />
            <button
              type="submit"
              disabled={isResending}
              className="w-full flex items-center justify-center gap-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-slate-900 font-bold py-3.5 rounded-xl text-sm transition-all shadow-sm"
            >
              <ArrowPathIcon className={`w-4 h-4 ${isResending ? 'animate-spin' : ''}`} />
              {isResending ? 'Sending...' : 'Send new verification email'}
            </button>
          </form>
        )}

        {/* Success / Already: go to login */}
        {(status === 'success' || status === 'already') && (
          <Link
            to="/login"
            className="block w-full bg-brand-500 hover:bg-brand-600 text-slate-900 font-bold py-3.5 rounded-xl text-sm transition-all shadow-sm text-center mb-6"
          >
            {config.cta}
          </Link>
        )}

        <p className="text-xs text-slate-400">
          Need help?{' '}
          <a
            href="mailto:support@localcontact.ai"
            className="font-semibold text-slate-500 hover:text-slate-800 transition-colors"
          >
            Contact support
          </a>
        </p>
      </div>
    </AuthLayout>
  );
}
